/* eslint-disable react-refresh/only-export-components -- this file
 * intentionally exports both the DateRangePicker component AND the
 * `useDateRange` hook + `parseRange` helper that callers rely on. The
 * hook is colocated with the component so they share window-key
 * constants; splitting into a second file would force every consumer
 * to import from two paths for what is logically one widget. The
 * hot-reload caveat the rule warns about doesn't matter here — the
 * hook resets to defaults on reload regardless. */
import { useState, useRef, useEffect, useMemo } from 'react'
import { Calendar, ChevronDown } from 'lucide-react'
import PpcFreshnessChip from './PpcFreshnessChip'
import { accountNow, daysAgoYMD, isoYMD as isoYMDShared, todayYMD } from '../lib/format/date'
import { usePpcDataThrough } from '../lib/ppcDataThrough'
import { reportGap, reportGapSentence } from '../lib/reportGap'

export type DateRangePreset =
  | 'today' | 'yesterday' | 'wtd' | 'last_week'
  | '7d' | '14d' | '30d' | '60d' | '90d' | '6mo' | '12mo'
  | 'mtd' | 'qtd' | 'ytd' | 'custom'
  // Calendar-relative presets (v2 #11 — shared by all PPC tabs + the P&L tab):
  //   this_month   = 1st of this month → today
  //   last_month   = 1st → last day of the previous calendar month
  //   this_quarter = 1st of this quarter → today
  //   last_quarter = 1st → last day of the previous calendar quarter
  //   lifetime     = a fixed far-past epoch → today ("all data we have")
  | 'this_month' | 'last_month' | 'this_quarter' | 'last_quarter' | 'lifetime'

export type DateRange = {
  from: string   // ISO YYYY-MM-DD
  to: string     // ISO YYYY-MM-DD
  preset: DateRangePreset
  /** Always 0 since PR 12d ("Exclude latest N days" is gone: a preset's dates
   *  are the window its name says, and the days Amazon has not reported are
   *  cut, lib/reportGap.ts). Kept so a pick stored by an older build still
   *  parses; every read ignores a stored non-zero value. */
  lag?: number
}

// UI overhaul PR-3b (audit APP-18) + PR 12 (C8): every "today"/"N days ago" is
// the ACCOUNT's calendar date, Pacific (lib/format/date.ts) - Amazon's day, not
// the browser's. `toISOString().slice(0, 10)` was the UTC date. Every helper
// below takes an optional `now` instant so a test pins the clock.
function isoDaysAgo(days: number, now?: Date): string {
  return daysAgoYMD(days, now)
}

function today(now?: Date): string {
  return todayYMD(now)
}

/** Subtract N days from an ISO date string (returns ISO). */
function isoMinusDays(iso: string, days: number): string {
  if (!iso || !days) return iso
  const [y, m, d] = iso.split('-').map(Number)
  const dt = new Date(y, m - 1, d)
  dt.setDate(dt.getDate() - days)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`
}

function firstOfMonth(now?: Date): string {
  const d = accountNow(now)
  return isoYMD(d.getFullYear(), d.getMonth(), 1)
}

function firstOfQuarter(now?: Date): string {
  const d = accountNow(now)
  return isoYMD(d.getFullYear(), Math.floor(d.getMonth() / 3) * 3, 1)
}

function firstOfYear(now?: Date): string {
  return isoYMD(accountNow(now).getFullYear(), 0, 1)
}

/** N calendar months ago, today's day-of-month. Clamps to end-of-month if needed. */
function monthsAgo(months: number, now?: Date): string {
  const d = accountNow(now)
  return isoYMD(d.getFullYear(), d.getMonth() - months, d.getDate())
}

/** Sunday of the current week (US convention — Amazon reports also use Sun–Sat). */
function startOfThisWeek(now?: Date): string {
  const d = accountNow(now)
  return isoYMD(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay())
}

/** Sunday of last week. */
function startOfLastWeek(now?: Date): string {
  const d = accountNow(now)
  return isoYMD(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay() - 7)
}

// ── Calendar-relative month / quarter helpers (v2 #11) ──────────────────────
// isoYMD below is the ONE way a calendar boundary is turned into a date here.
// It used to be applied only to the v2 #11 helpers while the older ones above
// still did `d.setDate(1); d.toISOString()` - local mutation, UTC formatting.
// In any UTC-negative locale that returns the WRONG DAY for the last hours of
// every day: at 20:18 EDT on 2026-09-08, firstOfMonth() returned 2026-09-02.
// mtd / this_month / qtd / ytd / this_week / last_week all inherited it.
// All return YYYY-MM-DD built from local Y/M/D parts so a "1st of month" never
// drifts across a timezone boundary (toISOString shifts UTC-negative locales a
// day back at midnight).
function isoYMD(y: number, m0: number, d: number): string {
  // The shared implementation (lib/format/date.ts) — kept under the local name
  // so every helper above reads the same as before.
  return isoYMDShared(y, m0, d)
}

/** 1st of the previous calendar month. */
function startOfLastMonth(now?: Date): string {
  const d = accountNow(now)
  return isoYMD(d.getFullYear(), d.getMonth() - 1, 1)
}

/** Last day of the previous calendar month (day 0 of this month). */
function endOfLastMonth(now?: Date): string {
  const d = accountNow(now)
  return isoYMD(d.getFullYear(), d.getMonth(), 0)
}

/** 1st of the previous calendar quarter. */
function startOfLastQuarter(now?: Date): string {
  const d = accountNow(now)
  const q0 = Math.floor(d.getMonth() / 3) * 3   // first month of THIS quarter
  return isoYMD(d.getFullYear(), q0 - 3, 1)
}

/** Last day of the previous calendar quarter (day 0 of this quarter's 1st month). */
function endOfLastQuarter(now?: Date): string {
  const d = accountNow(now)
  const q0 = Math.floor(d.getMonth() / 3) * 3
  return isoYMD(d.getFullYear(), q0, 0)
}

/** Lifetime epoch — a fixed far-past date covering all data we have. The P&L
 *  rollup history starts ~2025-05; 2000-01-01 safely spans any product's
 *  inception while keeping the preset a concrete from/to like every other. */
const LIFETIME_EPOCH = '2000-01-01'

/** Saturday of last week. */
function endOfLastWeek(now?: Date): string {
  const d = accountNow(now)
  return isoYMD(d.getFullYear(), d.getMonth(), d.getDate() - d.getDay() - 1)
}

/** Compute concrete from/to for a preset, today inclusive. The `lag` knob
 *  shifts EVERY preset back by N days, including single-day picks like
 *  'today' / 'yesterday' — operator's mental model is "lag = how many days
 *  to subtract from the displayed window," and the chart always follows
 *  the picker. `custom` ignores the lag — the user typed explicit dates
 *  and we don't second-guess them. */
export function rangeFromPreset(preset: DateRangePreset, lag = 0, now?: Date): DateRange {
  const base = rangeFromPresetBase(preset, now)
  if (!lag || preset === 'custom') return { ...base, lag }
  return {
    from: isoMinusDays(base.from, lag),
    to:   isoMinusDays(base.to,   lag),
    preset,
    lag,
  }
}

function rangeFromPresetBase(preset: DateRangePreset, n?: Date): DateRange {
  const t = today(n)
  switch (preset) {
    case 'today':     return { from: t, to: t, preset }
    case 'yesterday': return { from: isoDaysAgo(1, n), to: isoDaysAgo(1, n), preset }
    case 'wtd':       return { from: startOfThisWeek(n), to: t, preset }
    case 'last_week': return { from: startOfLastWeek(n), to: endOfLastWeek(n), preset }
    case '7d':        return { from: isoDaysAgo(6, n),  to: t, preset }
    case '14d':       return { from: isoDaysAgo(13, n), to: t, preset }
    case '30d':       return { from: isoDaysAgo(29, n), to: t, preset }
    case '60d':       return { from: isoDaysAgo(59, n), to: t, preset }
    case '90d':       return { from: isoDaysAgo(89, n), to: t, preset }
    case '6mo':       return { from: monthsAgo(6, n),  to: t, preset }
    case '12mo':      return { from: monthsAgo(12, n), to: t, preset }
    // `mtd` / `qtd` are aliases of this_month / this_quarter (C5: one entry in
    // the list); kept so a stored pick still resolves.
    case 'mtd':       return { from: firstOfMonth(n), to: t, preset }
    case 'qtd':       return { from: firstOfQuarter(n), to: t, preset }
    case 'ytd':       return { from: firstOfYear(n),  to: t, preset }
    // Calendar-relative presets (v2 #11): this_* are the 1st-of-period -> today
    // windows; last_* are the full previous calendar month/quarter; lifetime
    // spans all history.
    case 'this_month':    return { from: firstOfMonth(n),   to: t, preset }
    case 'last_month':    return { from: startOfLastMonth(n), to: endOfLastMonth(n), preset }
    case 'this_quarter':  return { from: firstOfQuarter(n), to: t, preset }
    case 'last_quarter':  return { from: startOfLastQuarter(n), to: endOfLastQuarter(n), preset }
    case 'lifetime':      return { from: LIFETIME_EPOCH,   to: t, preset }
    case 'custom':    return { from: isoDaysAgo(29, n), to: t, preset }
  }
}

// Exported for the app-wide header control (components/AppDateRangeControl.tsx,
// SellerMate W0.3) so both pickers offer the same list in the same order.
export const PRESETS: Array<{ value: DateRangePreset; label: string }> = [
  { value: 'today',     label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'wtd',       label: 'This week' },
  { value: 'last_week', label: 'Last week' },
  { value: '7d',        label: 'Last 7 days' },
  { value: '14d',       label: 'Last 14 days' },
  { value: '30d',       label: 'Last 30 days' },
  { value: '60d',       label: 'Last 60 days' },
  { value: '90d',       label: 'Last 90 days' },
  { value: '6mo',       label: 'Last 6 months' },
  { value: '12mo',      label: 'Last 12 months' },
  { value: 'this_month',   label: 'This month' },
  { value: 'last_month',   label: 'Last month' },
  { value: 'this_quarter', label: 'This quarter' },
  { value: 'last_quarter', label: 'Last quarter' },
  { value: 'ytd',       label: 'Year to date' },
  { value: 'lifetime',  label: 'Lifetime' },
  { value: 'custom',    label: 'Custom dates' },
]

/** Old presets kept only so a stored pick still resolves (C5): the list has one
 *  "This month" / "This quarter", not two names for the same window. */
export const PRESET_ALIASES: Partial<Record<DateRangePreset, DateRangePreset>> = {
  mtd: 'this_month',
  qtd: 'this_quarter',
}

/** Presets that name ONE day. Never persisted (UI overhaul PR-3b, audit PPC-01):
 *  one "Today" pick used to be saved to the key every PPC page shared, and
 *  attributed reports lag ~2 days, so every table then opened empty for as
 *  long as the pick stayed saved. A single-day pick lives for the visit only. */
const NEVER_PERSISTED: ReadonlySet<DateRangePreset> = new Set(['today', 'yesterday'])

export function persistRange(storageKey: string, next: DateRange): void {
  if (NEVER_PERSISTED.has(next.preset)) return
  try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch { /* ignore */ }
}

/** A preset pick from the picker's menu: the window it names, remembered under the PAGE's own key. The picker's click handler is
 *  exactly this plus closing the menu; exported so the persistence rule is
 *  testable without a DOM (DateRangePicker.persist.test.tsx). */
export function applyPreset(storageKey: string, preset: DateRangePreset): DateRange {
  const next = rangeFromPreset(preset)
  persistRange(storageKey, next)
  return next
}

/** The window an ATTRIBUTED table may ask for: never past the last complete
 *  report day. The pick stays THE window (URL, header, tiles); the fetch cuts
 *  `to` back to the last reported day and never moves `from`. When no picked
 *  day is reported yet (Today, early-morning Yesterday) the latest report day
 *  stands in, and the header, strip and tiles say so (lib/reportGap.ts). */
export type EffectiveRange = DateRange & {
  /** The pick reached past the last complete report day: `to` was cut back, or (standIn) the latest report day stands in. */
  trimmed: boolean
  /** The last complete report day the clamp used, or null when unknown. */
  dataThrough: string | null
  /** True when no picked day is reported yet: from/to are the latest report day, standing in for the pick. */
  standIn: boolean
}

export function clampRange(range: DateRange, dataThrough: string | null): EffectiveRange {
  const gap = reportGap(range, dataThrough)
  if (gap.kind === 'complete') return { ...range, trimmed: false, dataThrough, standIn: false }
  if (gap.kind === 'partial') return { ...range, to: gap.through, trimmed: true, dataThrough, standIn: false }
  return { ...range, from: gap.standIn, to: gap.standIn, trimmed: true, dataThrough, standIn: true }
}

/**
 * Date-range picker for performance pages. Persists the user's choice to
 * localStorage under the page's own `storageKey`, so the page reopens on it.
 *
 * `storageKey` is REQUIRED and must be the same string the page's
 * `useDateRange` reads (C7, PR 3). It used to default to a key every PPC page
 * shared, so on Ad Groups, Placements, Product Ads, Products and Ads Overview
 * a pick was written under the shared key while the page loaded its own,
 * untouched one — every reload snapped back to 30 days. A required prop makes
 * the compiler find the next page that forgets. Declare the key once as a
 * constant in the page and use it in both places.
 *
 * Usage:
 *   const RANGE_KEY = 'adbetter.dateRange.hourly'
 *   const [range, setRange] = useDateRange(RANGE_KEY)
 *   <DateRangePicker range={range} onChange={setRange} storageKey={RANGE_KEY} />
 */
export default function DateRangePicker({
  range, onChange, showFreshness = true,
  storageKey, effective,
}: {
  range: DateRange
  onChange: (r: DateRange) => void
  /** The clamped window the page actually fetched (third value from
   *  `useDateRange`). When it differs from the pick, the picker says so. */
  effective?: EffectiveRange
  /** Render the PPC data-freshness chip to the left of the picker. Default true. */
  showFreshness?: boolean
  /** localStorage key for this page's range — the SAME key the page's
   *  `useDateRange` reads. Required: there is no shared default (C7). */
  storageKey: string
}) {
  const [open, setOpen] = useState(false)
  const [showCustom, setShowCustom] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    if (open) document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  function pickPreset(p: DateRangePreset) {
    if (p === 'custom') {
      setShowCustom(true)
      return
    }
    onChange(applyPreset(storageKey, p))
    setOpen(false)
    setShowCustom(false)
  }

  function applyCustom() {
    const next = { ...range, preset: 'custom' as const, lag: 0 }
    onChange(next)
    persistRange(storageKey, next)
    setOpen(false)
    setShowCustom(false)
  }

  const currentLabel = PRESETS.find(p => p.value === range.preset)?.label ?? 'Custom'
  const trimmedNote = effective?.trimmed
    ? reportGapSentence(reportGap(range, effective.dataThrough), Number(todayYMD().slice(0, 4)))
    : null
  const subLabel = range.preset === 'custom'
    ? `${range.from} → ${range.to}`
    : `${range.from} → ${range.to}`

  return (
    <div ref={ref} className="relative inline-flex items-center gap-2">
      {showFreshness && <PpcFreshnessChip />}
      {trimmedNote && (
        <span
          className="hidden xl:inline text-xs text-warning-soft-fg bg-warning-soft border border-warning-edge rounded-md px-2 py-0.5"
        >
          {trimmedNote}
        </span>
      )}
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 border border-edge-strong rounded text-sm hover:bg-surface-2"
      >
        <Calendar size={14} className="text-ink-muted" />
        <span>
          <span className="font-medium">{currentLabel}</span>
          <span className="text-xs text-ink-muted ml-2">{subLabel}</span>
        </span>
        <ChevronDown size={12} className="text-ink-faint" />
      </button>

      {open && (
        <div className="absolute top-full right-0 mt-1 z-40 bg-overlay border border-edge rounded-md shadow-lg w-64">
          <div className="py-1">
            {PRESETS.map(p => (
              <button
                key={p.value}
                onClick={() => pickPreset(p.value)}
                className={`w-full text-left px-3 py-1.5 text-sm hover:bg-surface-3 ${
                  range.preset === p.value ? 'bg-accent-soft text-accent-soft-fg font-medium' : ''
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {showCustom && (
            <div className="border-t border-edge-subtle p-3 space-y-2">
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-ink-muted mb-1">From</label>
                <input
                  type="date" value={range.from} max={range.to}
                  onChange={e => onChange({ ...range, from: e.target.value, preset: 'custom' })}
                  className="w-full border border-edge-strong rounded px-2 py-1 text-sm"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-ink-muted mb-1">To</label>
                <input
                  type="date" value={range.to} min={range.from} max={today()}
                  onChange={e => onChange({ ...range, to: e.target.value, preset: 'custom' })}
                  className="w-full border border-edge-strong rounded px-2 py-1 text-sm"
                />
              </div>
              <button
                onClick={applyCustom}
                className="w-full px-3 py-1.5 bg-accent text-accent-fg text-sm rounded hover:bg-accent-hover"
              >
                Apply
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Hook: load the persisted date range from localStorage, defaulting to 30d.
 *
 *  `storageKey` names the page's own entry (required; see useDateRange). */

/** One-time migration (ClickPay V1 #4): the PPC date-range lag default changed
 *  from 2 to 0. A persisted range carries its own `lag`, so a browser that
 *  already stored lag=2 would keep showing it until the user re-picks a preset.
 *  Clear the persisted date-range entries ONCE so the new lag=0 default takes
 *  effect immediately (a one-time reset of the remembered preset — acceptable). */
const LAG_RESET_KEY = 'adbetter.dateRange.lagReset.v1'
function migrateLagDefaultOnce(): void {
  try {
    if (typeof window === 'undefined') return
    if (localStorage.getItem(LAG_RESET_KEY)) return
    const toClear: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith('adbetter.dateRange') && k !== LAG_RESET_KEY) toClear.push(k)
    }
    toClear.forEach(k => localStorage.removeItem(k))
    localStorage.setItem(LAG_RESET_KEY, '1')
  } catch { /* ignore */ }
}

/**
 * The pick remembered under `storageKey`, with a preset's dates re-derived so
 * "Last 30 days" always ends today (a custom range keeps its typed dates).
 * Falls back to `defaultPreset` when nothing usable is stored: an empty or
 * blocked storage, bad JSON, a never-persisted single-day pick, or a preset
 * this build does not know (a newer build's value must not crash an older one).
 * Shared by `useDateRange` and the app-wide range (lib/dateRangeContext.tsx).
 */
export function loadStoredRange(
  storageKey: string,
  defaultPreset: DateRangePreset = '30d',
  now?: Date,
): DateRange {
  migrateLagDefaultOnce()
  try {
    const raw = localStorage.getItem(storageKey)
    if (raw) {
      const parsed = JSON.parse(raw) as DateRange
      parsed.preset = PRESET_ALIASES[parsed.preset] ?? parsed.preset
      const known = PRESETS.some(p => p.value === parsed.preset)
      if (parsed.from && parsed.to && known && !NEVER_PERSISTED.has(parsed.preset)) {
        // Re-derive concrete dates from preset so "30d" stays current.
        // A stored `lag` (the retired "Exclude latest N days") is ignored (PR 12d).
        if (parsed.preset !== 'custom') return rangeFromPreset(parsed.preset, 0, now)
        return { ...parsed, lag: 0 }
      }
    }
  } catch { /* fall through */ }
  return rangeFromPreset(defaultPreset, 0, now)
}

/**
 * A page's date range: the pick, its setter, and the window the page should
 * actually FETCH.
 *
 * `storageKey` is required (UI overhaul PR-3b): the old default key was shared
 * by every PPC page, so one pick on Campaigns silently changed Targets. Name
 * the page: `adbetter.dateRange.campaigns`.
 *
 * `clampTo: 'ppc'` (the attributed PPC pages) returns a third value clamped to
 * the last complete report day (`lib/ppcDataThrough.ts`); other sources (the
 * hourly stream, the P&L) get the pick back unchanged.
 *
 * `defaultPreset` is the window a page opens on the FIRST time it is used —
 * before the reader has picked anything. It defaults to '30d', which is right
 * for the daily-refreshed PPC sources but wrong for a surface whose backend
 * looks back a year (#82: Brand Analytics' Search Terms, whose endpoint
 * defaults to 365 days — opening its new picker on 30d would have SHRUNK the
 * window the reader already had and made the "add a date range" ticket read
 * as data loss). A stored pick always wins over this.
 */
export function useDateRange(
  storageKey: string,
  opts?: { clampTo?: 'ppc'; defaultPreset?: DateRangePreset },
): [DateRange, (r: DateRange) => void, EffectiveRange] {
  const [range, setRange] = useState<DateRange>(
    () => loadStoredRange(storageKey, opts?.defaultPreset),
  )
  const dataThrough = usePpcDataThrough()
  const clampTo = opts?.clampTo
  const effective = useMemo<EffectiveRange>(
    () => (clampTo === 'ppc' ? clampRange(range, dataThrough) : { ...range, trimmed: false, dataThrough: null, standIn: false }),
    [range, dataThrough, clampTo],
  )
  return [range, setRange, effective]
}
